// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import "../assets/css/herramientas.css";
import CajaDibujo from "../components/taller/CajaDibujo";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import {
  agruparCajas,
  aspectoDeCaja,
  COLORES_POR_EMPRESA,
  COLOR_SIN_EMPRESA,
  empresasDe,
  partesDelNombre,
  cuando,
  cuentaPanol,
  DESTINATARIOS,
  diaAr,
  entregasAbiertas,
  entregasDelPanol,
  faltaFoto,
  nombreDe,
  pelar,
  personas as traerPersonas,
  puedeVerEntregas,
} from "../services/herramientas";

/* Herramientas: una caja por persona.
 *
 * ── POR QUÉ NO ARRANCA EN EL TABLERO ─────────────────────────────────────────
 *
 * Nico, 02/10: *«está mal pensada»*. El tablero compartido —una pared de la
 * que se saca y se devuelve— es el 10% del taller. El 90% es que cada
 * mecánico o chofer tiene **su caja de herramientas asignada**: las tiene él,
 * son de él, no vuelven y no se demoran.
 *
 * Así que la pantalla arranca en las cajas. Tocar una abre su tablero, sólo
 * con lo que esa persona tiene. El pañol —lo que se presta y vuelve, y que sí
 * puede quedar demorado— es una tarjeta más, al final, y abre la pared de
 * siempre.
 *
 * ── SIN TÍTULO PROPIO ────────────────────────────────────────────────────────
 *
 * El marco ya dice «Herramientas» arriba, con su subtítulo. Un segundo
 * «Herramientas» abajo era el mismo cartel dos veces (Igna, 02/10: «no me
 * gusta como se ve»). La primera fila es directamente lo que se hace acá:
 * buscar una caja o armar una.
 *
 * ── LA DEMORA ES SÓLO DE LO PRESTADO ────────────────────────────────────────
 *
 * Una caja no muestra demoras: lo asignado no tiene plazo. Las demoradas que
 * cuenta la tarjeta del pañol son sólo entregas NO definitivas pasadas de su
 * plazo (`cuentaPanol`), la misma regla que la vista `taller_tablero`.
 *
 * ── MECÁNICOS Y CHOFERES EN EL MISMO ESTANTE (P-311) ────────────────────────
 *
 * Nico, 05/10: *«La sección herramienta y entrega de elementos deberían ser
 * la misma y verse como lo hace herramientas […] el destinatario es un chofer
 * y no un mecánico (única diferencia)»*. Las 65 actas de «Entrega de
 * elementos» pasaron a ser entregas del pañol (`panol_entregas`), y lo que
 * tienen escrito arma la caja del chofer igual que una herramienta arma la
 * del mecánico. *«Las cajas no hay problema que estén todos los talleres y el
 * puerto mezclados»*: el estante es uno solo; lo que se separa por pañol es
 * la pared de préstamos.
 *
 * Arriba se filtra por quién (mecánicos, choferes) y por lo que falta: las
 * cajas con algún papel sin la foto firmada llevan la cuenta en la tapa.
 */

const QUIENES = [
  ["", "Todas"],
  ["mecanico", `${DESTINATARIOS.mecanico.icono} Mecánicos`],
  ["chofer", `${DESTINATARIOS.chofer.icono} Choferes`],
  ["falta", "✍️ Falta el papel firmado"],
];

/* Las actas que no tienen a nadie del padrón (35 de las 64 de papel no
   tenían DNI legible): no arman caja —no se le adjudican a nadie por
   parecido— pero tampoco desaparecen. Abrirlas permite elegir de quién son. */
function ActasSinPersona({ entregas }) {
  if (!entregas.length) return null;
  return (
    <details className="hrr-sin-persona">
      <summary>
        {entregas.length} papel{entregas.length === 1 ? "" : "es"} sin una persona del padrón
        — tocá uno para decir de quién es
      </summary>
      <ul>
        {entregas.map((e) => (
          <li key={e.id}>
            <Link to={`/taller/herramientas/constancia/${e.id}`}>
              <b>{e.persona_nombre}</b>
              <small>
                {e.interno ? ` · interno ${e.interno}` : ""}
                {e.entregado_en ? ` · ${diaAr(e.entregado_en)}` : " · sin fecha"}
              </small>
            </Link>
          </li>
        ))}
      </ul>
    </details>
  );
}

function NuevaCaja({ onElegir, onCerrar }) {
  const [busca, setBusca] = useState("");
  const [gente, setGente] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState("");

  /* Recién con dos letras: el padrón entero en orden alfabético no ayuda a
     encontrar a nadie, y la primera letra sola trae medio padrón. */
  const corta = busca.trim().length < 2;

  useEffect(() => {
    if (corta) { setGente([]); setError(""); setBuscando(false); return undefined; }
    let vivo = true;
    setBuscando(true);
    const t = setTimeout(async () => {
      const r = await traerPersonas(busca);
      if (!vivo) return;
      setGente(r.filas || []);
      setError(r.error || "");
      setBuscando(false);
    }, 200);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca, corta]);

  return (
    <section className="hrr-caja-nueva" aria-label="Nueva caja">
      <div className="hrr-caja-nueva-titulo">
        <b>Nueva caja · ¿de quién es?</b>
        <button type="button" className="hrr-btn hrr-btn--chico" onClick={onCerrar}>
          Cancelar
        </button>
      </div>
      <input
        type="search"
        autoFocus
        placeholder="Apellido, nombre o DNI — por ejemplo «apellido nombre»"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        aria-label="Buscar persona"
      />
      {error && <p className="hrr-aviso">No se pudo consultar.</p>}
      {!corta && !buscando && !error && gente.length === 0 && (
        <p className="tenue hrr-caja-nadie">No encontré a nadie con «{busca.trim()}».</p>
      )}
      {gente.length > 0 && (
        <ul className="hrr-caja-gente">
          {gente.slice(0, 12).map((p) => (
            <li key={p.dni}>
              <button type="button" onClick={() => onElegir(p)}>
                <b>{nombreDe(p)}</b>
                <small>DNI {p.dni}{p.sector ? ` · ${p.sector}` : ""}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const CajasHerramientas = () => {
  const { permisos = null } = useOutletContext() || {};
  const permitido = puedeVerEntregas(permisos);
  const puedeEditar = permisos !== null && permisos.includes("cargar.taller");
  const ir = useNavigate();
  const [params, setParams] = useSearchParams();
  const quien = QUIENES.some(([v]) => v === params.get("quien")) ? params.get("quien") : "";
  const elegirQuien = (v) => {
    const otros = new URLSearchParams(params);
    if (v) otros.set("quien", v); else otros.delete("quien");
    setParams(otros, { replace: true });
  };

  const [filas, setFilas] = useState(undefined); // undefined = cargando
  const [entregas, setEntregas] = useState([]);
  const [error, setError] = useState("");
  const [texto, setTexto] = useState("");
  const [nueva, setNueva] = useState(false);
  const [empresas, setEmpresas] = useState({}); // dni → CUIT de su empresa
  const [puestos, setPuestos] = useState({}); // dni → puesto del padrón

  useEffect(() => {
    if (!permitido) return undefined;
    let vivo = true;
    Promise.all([entregasAbiertas(), entregasDelPanol()]).then(async ([r, en]) => {
      if (!vivo) return;
      setFilas(r.filas || []);
      setEntregas(en.filas || []);
      setError(r.error || en.error || "");
      /* El color de cada caja es el de la empresa de la persona. Se pide
         después y aparte: si no llega, las cajas se ven igual —galvanizadas—
         y nadie se queda sin ver la suya. */
      const dnis = [
        ...(r.filas || []).filter((f) => f.definitivo).map((f) => f.persona_dni),
        ...(en.filas || []).map((f) => f.persona_dni),
      ];
      const e = await empresasDe(dnis);
      if (vivo) {
        setEmpresas(e.porDni || {});
        setPuestos(e.puestoPorDni || {});
      }
    });
    return () => { vivo = false; };
  }, [permitido]);

  /* Mecánico o chofer: lo que dice HOY el padrón; si no lo dice, el tipo de
     su última entrega; si tampoco, mecánico (lo que era esta pantalla). */
  const todas = useMemo(() => agruparCajas(filas || [], entregas).map((c) => ({
    ...c,
    destinatario: puestos[String(c.dni)]
      ? (pelar(puestos[String(c.dni)]) === "CHOFER" ? "chofer" : "mecanico")
      : (c.destinatario || "mecanico"),
  })), [filas, entregas, puestos]);
  const sinPersona = useMemo(
    () => entregas.filter((e) => !String(e.persona_dni || "").trim() && !e.anulado_en),
    [entregas]);
  const faltanPapeles = useMemo(
    () => entregas.filter((e) => String(e.persona_dni || "").trim() && faltaFoto(e)).length,
    [entregas]);
  const panol = useMemo(() => (filas && !error ? cuentaPanol(filas) : null),
                        [filas, error]);
  const visibles = useMemo(() => {
    const t = pelar(texto);
    return todas
      .filter((c) => !quien || (quien === "falta" ? c.sinFoto > 0 : c.destinatario === quien))
      .filter((c) => !t || pelar(`${c.nombre} ${c.dni || ""}`).includes(t));
  }, [todas, texto, quien]);

  if (!permitido) {
    return <PuertaCerrada />;
  }

  const abrir = (dni) => ir(`/taller/herramientas/caja/${encodeURIComponent(dni)}`);
  const sinCajas = filas && !error && todas.length === 0;

  return (
    <div className="hrr hrr-cajas-pantalla">
      <div className="hrr-cajas-barra">
        <input
          type="search"
          className="hrr-cajas-buscar"
          placeholder={todas.length
            ? `Buscar entre ${todas.length} caja${todas.length === 1 ? "" : "s"}`
            : "Buscar una caja por nombre o DNI"}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          aria-label="Buscar caja"
          disabled={sinCajas}
        />
        {puedeEditar && !nueva && (
          <button type="button" className="hrr-btn hrr-btn--principal hrr-btn--chico"
                  onClick={() => setNueva(true)}>
            ➕ Nueva caja
          </button>
        )}
        {puedeEditar && (
          <Link className="hrr-btn hrr-btn--chico" to="/taller/herramientas/administrar">
            ✎ Catálogo
          </Link>
        )}
      </div>

      {nueva && (
        <NuevaCaja onElegir={(p) => abrir(p.dni)} onCerrar={() => setNueva(false)} />
      )}

      {todas.length > 0 && (
        <div className="hrr-quienes" role="group" aria-label="Qué cajas ver">
          {QUIENES.map(([v, rotulo]) => (
            <button key={v || "todas"} type="button" className="hrr-chip"
                    aria-pressed={quien === v} onClick={() => elegirQuien(v)}>
              {rotulo}
              {v === "falta" && faltanPapeles > 0 ? ` (${faltanPapeles})` : ""}
            </button>
          ))}
        </div>
      )}

      {/* Una consulta caída NO se dibuja como «nadie tiene caja». */}
      {error && (
        <p className="hrr-aviso">No se pudo consultar.</p>
      )}
      {filas === undefined && <p className="hrr-vacio">Cargando las cajas…</p>}
      {filas && todas.length > 0 && visibles.length === 0 && (
        <p className="tenue hrr-caja-nadie">
          {texto ? `Ninguna caja coincide con «${texto}».` : "No hay cajas con ese filtro."}
        </p>
      )}

      {filas !== undefined && (
        <>
          {/* EL PAÑOL ES EL CARTEL DE ARRIBA DEL ESTANTE, y está siempre: es
              lo único que existe desde el día uno, y con cero cajas no queda
              flotando solo abajo de un aviso vacío. */}
          <Link className="hrr-cartel-panol" to="/taller/herramientas/panol">
            <span className="hrr-cartel-panol-icono" aria-hidden="true">🔧</span>
            <b>Pañol · préstamos</b>
            {panol ? (
              <small>
                {panol.prestadas} prestada{panol.prestadas === 1 ? "" : "s"}
                {panol.demoradas > 0 && (
                  <span className="hrr-caja-tarde">
                    {` · ${panol.demoradas} demorada${panol.demoradas === 1 ? "" : "s"}`}
                  </span>
                )}
              </small>
            ) : (
              <small>Lo que se presta y vuelve</small>
            )}
            <span className="hrr-cartel-panol-ir" aria-hidden="true">→</span>
          </Link>

          {/* EL ESTANTE (Igna, 02/10, con imagen de referencia): una
              estantería de metal con una caja de chapa por persona, y el
              nombre en la chapita del frente. El color es
              el de la empresa de la persona (`aspectoDeCaja`). */}
          <div className="hrr-estante">
            <ul className="hrr-estante-cajas">
              {visibles.map((c) => {
                const { apellido, nombres } = partesDelNombre(c.nombre);
                const { color } = aspectoDeCaja(empresas[String(c.dni)]);
                const d = DESTINATARIOS[c.destinatario] || DESTINATARIOS.mecanico;
                return (
                  <li key={c.dni || c.nombre}>
                    <button type="button" className="hrr-cajita"
                            onClick={() => abrir(c.dni || c.nombre)}
                            title={c.desde ? `${c.nombre} · desde ${cuando(c.desde)}` : c.nombre}
                            aria-label={`Caja de ${c.nombre}: ${c.herramientas} ` +
                                        `${c.destinatario === "chofer" ? "elemento(s)" : "herramienta(s)"}` +
                                        ` · ${d.rotulo.toLowerCase()}` +
                                        (c.sinFoto ? ` · ${c.sinFoto} papel(es) sin firmar` : "")}>
                      <span className="hrr-cajita-dibujo">
                        <CajaDibujo color={color} />
                        <span className="hrr-cajita-chapa">
                          <b>{apellido}</b>
                          {nombres && <small>{nombres}</small>}
                        </span>
                        {/* Cuántas tiene, en una chapita oscura sobre la tapa:
                            debajo de la caja tapaba la viga del estante. El
                            detalle va en el `title` y en el aria-label. */}
                        <span className="hrr-cajita-cuenta" aria-hidden="true"
                              title={`${c.herramientas} herramienta${c.herramientas === 1 ? "" : "s"}` +
                                     (c.unidades !== c.herramientas ? ` · ${c.unidades} unidades` : "")}>
                          {c.herramientas}
                        </span>
                        {/* Mecánico o chofer, y si le falta algún papel firmado:
                            dos chapitas chicas en la otra esquina de la tapa. */}
                        <span className="hrr-cajita-quien" aria-hidden="true" title={d.rotulo}>
                          {d.icono}
                        </span>
                        {c.sinFoto > 0 && (
                          <span className="hrr-cajita-papel" aria-hidden="true"
                                title={`${c.sinFoto} papel${c.sinFoto === 1 ? "" : "es"} sin la foto firmada`}>
                            ✍️{c.sinFoto}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
              {/* EL LUGAR LIBRE DEL ESTANTE: el que puede armar una caja ve un
                  hueco punteado al final; sin cajas, es lo único que hay. */}
              {puedeEditar && !texto && (
                <li>
                  <button type="button" className="hrr-cajita hrr-cajita--libre"
                          onClick={() => setNueva(true)}
                          aria-label={sinCajas ? "Todavía no hay cajas: armar la primera"
                                               : "Armar una caja nueva"}>
                    <span className="hrr-cajita-dibujo hrr-cajita-hueco">
                      <span aria-hidden="true">➕</span>
                      <b>{sinCajas ? "Todavía no hay cajas" : "Nueva caja"}</b>
                    </span>
                    {sinCajas && <span className="hrr-cajita-pie">Tocá acá para armar la primera</span>}
                  </button>
                </li>
              )}
              {sinCajas && !puedeEditar && (
                <li>
                  <div className="hrr-cajita hrr-cajita--libre">
                    <span className="hrr-cajita-dibujo hrr-cajita-hueco">
                      <b>Todavía no hay cajas</b>
                    </span>
                    <span className="hrr-cajita-pie">Nadie tiene herramientas asignadas</span>
                  </div>
                </li>
              )}
            </ul>
          </div>
          {todas.length > 0 && (
            <p className="hrr-estante-leyenda" aria-label="Colores por empresa">
              {Object.entries(COLORES_POR_EMPRESA).map(([sigla, color]) => (
                <span key={sigla}><i className={`hrr-pintura hrr-pintura--${color}`} />{sigla}</span>
              ))}
              <span><i className={`hrr-pintura hrr-pintura--${COLOR_SIN_EMPRESA}`} />Sin empresa</span>
            </p>
          )}
          <ActasSinPersona entregas={sinPersona} />
        </>
      )}
    </div>
  );
};

export default CajasHerramientas;
